import axios from 'axios';
import { logger } from '../utils/logger';
import { getAccessToken, clearAccessToken, clearLegacyTokenStorage } from '../utils/authToken';
import { csrfHeaders, captureCsrfFromAuthPayload, clearCsrfToken } from '../utils/csrf';
import { refreshAccessToken } from '../utils/sessionRefresh';
import {
  EmployeeData,
  ServiceProviderData,
  LocationData
} from '../types/common';
import { AdditionalLocation, CriticalFunction, KeySupplier, ITSystem } from '../types/bcpForm';
import { CSVImportRequest, CSVImportResponse, CSVTemplateResponse, CSVTemplatesResponse, ImportType } from '../types/csvImport';
import { TableTopExercise, TableTopExerciseUpdate, GenerateByCategory } from '../types/tabletopExercise';
import { resolveApiBaseUrl } from '../utils/apiBaseUrl';

const API_BASE_URL = resolveApiBaseUrl(process.env.REACT_APP_API_URL);

function applyResolvedBaseUrl(config: { baseURL?: string }) {
  config.baseURL = resolveApiBaseUrl(process.env.REACT_APP_API_URL);
  return config;
}

export const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 60000, // 1 minute timeout for AI requests
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Separate API instance for AI requests with longer timeout
export const aiApi = axios.create({
  baseURL: API_BASE_URL,
  timeout: 900000, // 1.5 minute timeout for AI requests
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

clearLegacyTokenStorage();

function captureAuthCsrf(response: { data?: unknown }) {
  captureCsrfFromAuthPayload(response.data);
}

export function authHeaders(): Record<string, string> {
  const token = getAccessToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

// Force instance defaults to HTTPS when page is HTTPS or on production frontend
if (typeof window !== 'undefined') {
  const forceHttps = window.location?.protocol === 'https:' || /flashprepared\.com|flash-em\.com/i.test(window.location?.hostname || '');
  if (forceHttps) {
    [api, aiApi].forEach((instance) => {
      const b = instance.defaults.baseURL;
      if (typeof b === 'string' && b.startsWith('http://')) {
        instance.defaults.baseURL = b.replace('http://', 'https://');
      }
    });
  }
}

// Add the same interceptors to aiApi
aiApi.interceptors.request.use((config) => {
  applyResolvedBaseUrl(config);
  logger.apiRequest(`${config.method?.toUpperCase()} ${config.url}`, config.method || 'GET', {
    baseURL: config.baseURL,
    headers: config.headers
  });

  const token = getAccessToken();
  if (token) {
    config.headers = config.headers || {};
    (config.headers as any)['Authorization'] = `Bearer ${token}`;
    logger.debug('Authorization header added', undefined, 'API');
  }

  return config;
});

async function handleUnauthorized(error: any, retryClient: typeof api) {
  const url = error.config?.url || '';
  const isAuthAttempt =
    url.includes('/auth/login') ||
    url.includes('/auth/register') ||
    url.includes('/auth/magic-link') ||
    url.includes('/password-reset');

  if (isAuthAttempt) {
    return Promise.reject(error);
  }

  if (error.response?.status === 401 && !error.config?.url?.includes('/refresh')) {
    const newToken = await refreshAccessToken();
    if (newToken) {
      error.config.headers['Authorization'] = `Bearer ${newToken}`;
      logger.debug('Token refreshed, retrying request...', undefined, 'API');
      return retryClient.request(error.config);
    }
    clearAccessToken();
    window.location.href = '/login';
  }
  return Promise.reject(error);
}

aiApi.interceptors.response.use(
  (response) => {
    captureAuthCsrf(response);
    logger.apiResponse(`${response.config.method?.toUpperCase()} ${response.config.url}`, response.status, response.data);
    return response;
  },
  async (error) => {
    logger.apiError(`${error.config?.method?.toUpperCase()} ${error.config?.url}`, error.response?.status || 0, {
      statusText: error.response?.statusText,
      data: error.response?.data,
      message: error.message,
      code: error.code
    });

    return handleUnauthorized(error, aiApi);
  }
);

api.interceptors.request.use((config) => {
  applyResolvedBaseUrl(config);
  logger.apiRequest(`${config.method?.toUpperCase()} ${config.url}`, config.method || 'GET', {
    baseURL: config.baseURL,
    headers: config.headers
  });

  const token = getAccessToken();
  if (token) {
    config.headers = config.headers || {};
    (config.headers as any)['Authorization'] = `Bearer ${token}`;
    logger.debug('Authorization header added', undefined, 'API');
  }

  return config;
});

// Response interceptor for token refresh and logging
api.interceptors.response.use(
  (response) => {
    captureAuthCsrf(response);
    logger.apiResponse(`${response.config.method?.toUpperCase()} ${response.config.url}`, response.status, response.data);
    return response;
  },
  async (error) => {
    logger.apiError(`${error.config?.method?.toUpperCase()} ${error.config?.url}`, error.response?.status || 0, {
      statusText: error.response?.statusText,
      data: error.response?.data,
      message: error.message,
      code: error.code
    });

    return handleUnauthorized(error, api);
  }
);

export type AnalysisStruct = {
  risks: string[];
  impacts: string[];
  mitigations: string[];
  missing_info: string[];
  industry_insights?: string[];
  compliance_notes?: string[];
  recovery_priorities?: string[];
};
export type AnalyzeBCPResponse = { bcp_id: number; analysis: AnalysisStruct; raw?: string };

// Auth functions
export async function logout() {
  const { data } = await api.post('/v1/auth/logout', {}, { headers: csrfHeaders() });
  clearAccessToken();
  clearCsrfToken();
  return data;
}

export async function analyzeBCP(bcpId: number, overrideText?: string) {
  const { data } = await api.post(`/v1/bcp/${bcpId}/analyze`, overrideText ? { override_text: overrideText } : undefined);
  return data as AnalyzeBCPResponse;
}

export type BCPSuggestions = {
  suggested_risks: string[];
  suggested_critical_functions: string[];
  location_specific_risks: string[];
  industry_specific_functions: string[]; // This includes mitigation strategies
  business_size_considerations: string[];
};

export type GetSuggestionsResponse = {
  suggestions: BCPSuggestions;
  business_profile: {
    name: string;
    type: string;
    industry: string;
    location: string;
    size: number;
  };
};

export async function getBCPSuggestions(bcpId: string | number) {
  const { data } = await aiApi.post(`/v1/bcp/${bcpId}/suggest`);
  return data as GetSuggestionsResponse;
}

export async function getBCPRiskSuggestions(bcpId: string | number) {
  const { data } = await aiApi.post(`/v1/bcp/${bcpId}/suggest-risks`);
  return data as GetSuggestionsResponse;
}

export async function getBCPMitigationSuggestions(
  bcpId: string | number,
  currentFormData: Record<string, any>
) {
  const { data } = await aiApi.post(`/v1/bcp/${bcpId}/suggest-mitigations`, {
    current_form_data: currentFormData
  });
  return data;
}

export async function getContextSpecificSuggestions(
  bcpId: string | number,
  contextType: string,
  currentFormData: Record<string, any>
) {
  const { data } = await aiApi.post(`/v1/bcp/${bcpId}/suggest-context`, {
    context_type: contextType,
    current_form_data: currentFormData
  });
  return data;
}

export async function analyzeFormData(formData: Record<string, unknown>) {
  const { data } = await aiApi.post(`/v1/bcp/analyze-form`, formData);
  return data;
}

export type CreateBCPPayload = {
  business_name: string;
  business_type: string;
  industry: string;
  employee_count: number;
  annual_revenue?: string;
  address: string;
  city: string;
  state: string;
  zip_code: string;
  country?: string;
  risk_factors?: Record<string, any>;
  threat_probability?: Record<string, any>;
  primary_location?: {
    name?: string;
    contact?: string;
    address?: string;
    city?: string;
    state?: string;
    zip?: string;
    phone?: string;
  };
  additional_locations?: Array<{
    name?: string;
    contact?: string;
    address?: string;
    city?: string;
    state?: string;
    zip?: string;
    phone?: string;
  }>;
};

export async function createBCP(payload: CreateBCPPayload) {
  const { data } = await api.post(`/v1/bcp/`, payload);
  return data as { id: number } & CreateBCPPayload;
}

export async function getBCPLimits() {
  const { data } = await api.get(`/v1/bcp/limits`);
  return data;
}

export type BCP = {
  id: number;
  business_name: string;
  business_type: string;
  industry: string;
  employee_count: number;
  annual_revenue: string;
  address: string;
  city: string;
  state: string;
  zip_code: string;
  country: string;
  status: string;
  created_at: string;
  updated_at?: string;
  // JSON fields from backend
  primary_location?: LocationData;
  additional_locations?: AdditionalLocation[];
  critical_functions?: CriticalFunction[];
  key_suppliers?: KeySupplier[];
  it_systems?: ITSystem[];
  mitigations?: any[]; // Mitigation array from backend
  risk_factors?: { selected: string[] };
  threat_probability?: { selected: string[] };
  max_downtime?: string;
  recovery_priority?: string;
  rpo?: string;
  ai_analysis?: AnalysisStruct; // Store AI analysis results
  // Business Impact Analysis fields
  financial_impact_severity?: 'minor' | 'moderate' | 'major' | 'catastrophic';
  operational_impact_severity?: 'minor' | 'moderate' | 'major' | 'catastrophic';
  reputational_impact_severity?: 'minor' | 'moderate' | 'major' | 'catastrophic';
  legal_regulatory_impact_severity?: 'minor' | 'moderate' | 'major' | 'catastrophic';
  financial_impact_description?: string;
  operational_impact_description?: string;
  reputational_impact_description?: string;
  legal_regulatory_impact_description?: string;
  // Risk Impact Assessments
  risk_impact_assessments?: {
    primary_concerns: { [riskName: string]: any };
    location_risks: { [riskName: string]: any };
  };
};

export async function getUserBCPs() {
  const { data } = await api.get(`/v1/bcp/`);
  return data as BCP[];
}

// Check if a BCP with the same business name already exists for the current user
export async function checkBCPDuplicate(businessName: string): Promise<boolean> {
  try {
    const bcps = await getUserBCPs();
    return bcps.some(bcp =>
      bcp.business_name.toLowerCase() === businessName.toLowerCase()
    );
  } catch (error) {
    console.error('Error checking for BCP duplicate:', error);
    return false; // If check fails, allow creation (backend will catch duplicates)
  }
}

// Employee API functions
export async function getEmployees(bcpId: number) {
  const { data } = await api.get(`/v1/bcp/${bcpId}/employees`);
  return data;
}

export async function createEmployee(bcpId: number, employeeData: Omit<EmployeeData, 'id'>) {
  const { data } = await api.post(`/v1/bcp/${bcpId}/employees`, employeeData);
  return data;
}

export async function updateEmployee(employeeId: number, employeeData: Partial<EmployeeData>) {
  const { data } = await api.put(`/v1/employees/${employeeId}`, employeeData);
  return data;
}

export async function deleteEmployee(employeeId: number) {
  const { data } = await api.delete(`/v1/employees/${employeeId}`);
  return data;
}

// Service Provider API functions
export async function getServiceProviders(bcpId: number) {
  const { data } = await api.get(`/v1/bcp/${bcpId}/service-providers`);
  return data;
}

export async function createServiceProvider(bcpId: number, providerData: Omit<ServiceProviderData, 'id'>) {
  const { data } = await api.post(`/v1/bcp/${bcpId}/service-providers`, providerData);
  return data;
}

export async function updateServiceProvider(providerId: number, providerData: Partial<ServiceProviderData>) {
  const { data } = await api.put(`/v1/service-providers/${providerId}`, providerData);
  return data;
}

export async function deleteServiceProvider(providerId: number) {
  const { data } = await api.delete(`/v1/service-providers/${providerId}`);
  return data;
}

export async function getUserBCP(id: number) {
  const { data } = await api.get(`/v1/bcp/${id}`);
  return data as BCP;
}

export async function updateBCP(id: number, payload: CreateBCPPayload) {
  const { data } = await api.put(`/v1/bcp/${id}`, payload);
  return data as BCP;
}

export const previewBCP = async (bcpId: number, isAdmin: boolean = false): Promise<string> => {
  const token = getAccessToken();
  const endpoint = isAdmin
    ? `${API_BASE_URL}/v1/admin/bcps/${bcpId}/preview`
    : `${API_BASE_URL}/v1/bcp/${bcpId}/preview`;
  const response = await fetch(endpoint, {
    method: 'GET',
    credentials: 'include',
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      'Content-Type': 'text/html',
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to preview BCP: ${response.statusText}`);
  }

  return response.text();
};

// CSV Import API functions
export async function importCSVData(bcpId: number, importRequest: CSVImportRequest): Promise<CSVImportResponse> {
  const { data } = await api.post(`/v1/bcp/${bcpId}/import-csv`, importRequest);
  return data;
}

export async function getCSVTemplate(importType: ImportType): Promise<CSVTemplateResponse> {
  const { data } = await api.get(`/v1/bcp/csv-templates/${importType}`);
  return data;
}

export async function getAllCSVTemplates(): Promise<CSVTemplatesResponse> {
  const { data } = await api.get('/v1/bcp/csv-templates');
  return data;
}

export function downloadCSVTemplate(csvContent: string, filename: string) {
  // Decode base64 content
  const binaryString = atob(csvContent);
  const bytes = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }

  // Create blob and download
  const blob = new Blob([bytes], { type: 'text/csv' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}

export function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      const result = reader.result as string;
      // Remove data:application/octet-stream;base64, prefix
      const base64 = result.split(',')[1];
      resolve(base64);
    };
    reader.onerror = error => reject(error);
  });
}

// ISO 27001 Tabletop Scenarios API (using existing tabletop exercise endpoints)
export async function generateTabletopScenarios(bcpId: number): Promise<TableTopExercise[]> {
  // This will be handled by the BCP analysis endpoint automatically
  // We can fetch the generated scenarios using the existing tabletop exercise API
  return await getTabletopScenariosForBCP(bcpId);
}

export async function generateAITabletopScenarios(bcpId: number, numberOfScenarios: number = 3, focusAreas?: string[]): Promise<any> {
  const { data } = await api.post(`/v1/tabletop-exercises/generate-ai-scenarios/${bcpId}`, {
    number_of_scenarios: numberOfScenarios,
    focus_areas: focusAreas
  });
  return data;
}

export async function getTabletopScenariosForBCP(bcpId: number): Promise<TableTopExercise[]> {
  const response = await api.get(`/v1/tabletop-exercises/bcp/${bcpId}`);
  return response.data;
}

export async function getTabletopScenario(scenarioId: number): Promise<TableTopExercise> {
  const response = await api.get(`/v1/tabletop-exercises/${scenarioId}`);
  return response.data;
}

export async function updateTabletopScenario(scenarioId: number, updateData: TableTopExerciseUpdate): Promise<TableTopExercise> {
  const response = await api.put(`/v1/tabletop-exercises/${scenarioId}`, updateData);
  return response.data;
}

export async function deleteTabletopScenario(scenarioId: number): Promise<{ message: string }> {
  const response = await api.delete(`/v1/tabletop-exercises/${scenarioId}`);
  return response.data;
}

export async function getUserTabletopScenarios(limit: number = 50, offset: number = 0): Promise<TableTopExercise[]> {
  const response = await api.get(`/v1/tabletop-exercises/?limit=${limit}&offset=${offset}`);
  return response.data;
}

export async function scheduleTabletopScenario(scenarioId: number, scheduledDate: string): Promise<{ message: string; scheduled_date: string }> {
  await api.put(`/v1/tabletop-exercises/${scenarioId}`, {
    scheduled_date: scheduledDate,
    status: 'scheduled'
  });
  return { message: 'Tabletop scenario scheduled successfully', scheduled_date: scheduledDate };
}

// Generate a SOW-category tabletop with optional standalone business_context
export async function generateTabletopByCategory(
  payload: GenerateByCategory
): Promise<TableTopExercise> {
  const { data } = await aiApi.post('/v1/tabletop-exercises/generate-by-category/', payload);
  return data;
}

// Trigger blank-form (DOCX) generation
export async function generateTabletopBlankForm(
  exerciseId: number
): Promise<{ task_id?: string; status: string; blank_form_url?: string }> {
  const { data } = await api.post(`/v1/tabletop-exercises/${exerciseId}/generate-blank-form/`);
  return data;
}

export async function getTabletopBlankFormStatus(
  exerciseId: number
): Promise<{ status: string; blank_form_url?: string; format?: string; generated_at?: string }> {
  const { data } = await api.get(`/v1/tabletop-exercises/${exerciseId}/blank-form-status/`);
  return data;
}

export function getTabletopBlankFormDownloadUrl(exerciseId: number): string {
  return `${(api.defaults.baseURL || '').replace(/\/$/, '')}/v1/tabletop-exercises/${exerciseId}/blank-form-file/`;
}

// Upload completed tabletop document (with optional audit request)
export async function uploadTabletopCompletedDocument(
  exerciseId: number,
  file: File,
  auditRequested: boolean = false,
  findingsSummary?: string
): Promise<{ message: string; audit_requested: boolean; status: string }> {
  const form = new FormData();
  form.append('file', file);
  form.append('audit_requested', auditRequested ? 'true' : 'false');
  if (findingsSummary) form.append('findings_summary', findingsSummary);
  const { data } = await api.post(
    `/v1/tabletop-exercises/${exerciseId}/upload-document/`,
    form,
    { headers: { 'Content-Type': 'multipart/form-data' } }
  );
  return data;
}

export async function completeTabletopScenario(
  scenarioId: number,
  findings?: Array<{ category: string; description: string; severity: 'low' | 'medium' | 'high' | 'critical' }>,
  recommendations?: Array<{ priority: 'low' | 'medium' | 'high' | 'critical'; description: string; responsible_party?: string; due_date?: string }>,
  lessons_learned?: string,
  action_items?: Array<{ description: string; responsible_party: string; due_date: string; status: 'pending' | 'in_progress' | 'completed' }>
): Promise<{ message: string }> {
  await api.put(`/v1/tabletop-exercises/${scenarioId}`, {
    findings,
    recommendations,
    lessons_learned,
    action_items,
    status: 'completed',
    completed_date: new Date().toISOString()
  });
  return { message: 'Tabletop scenario completed successfully' };
}


